import { isMemoryFull, markNudgeShown, nudgeAlreadyShown } from '@/lib/memory';
import { useEffect, useState } from 'react';

import { Button } from '@/components/ui/button';

import {
  useConversationMemory,
  useStartNewChat
} from './useConversationMemory';

/**
 * A suggestion to start a new chat, shown only when memory is full and at
 * most once per chat. It does not guess whether the topic changed: a full
 * memory is the one moment a new chat is clearly cheaper and sharper, and
 * the user is the one who knows what they are about to ask.
 */
export default function MemoryNudge() {
  const { state, chatKey } = useConversationMemory();
  const startNewChat = useStartNewChat();
  const [visible, setVisible] = useState(false);
  const full = isMemoryFull(state);

  useEffect(() => {
    if (!full) {
      setVisible(false);
      return;
    }
    if (chatKey && !nudgeAlreadyShown(chatKey)) {
      markNudgeShown(chatKey);
      setVisible(true);
    }
  }, [full, chatKey]);

  if (!visible || !state) return null;

  return (
    <div
      role="status"
      id="memory-nudge"
      className="w-full flex flex-wrap items-center justify-between gap-3 rounded-xl border px-3.5 py-2.5"
      style={{
        borderColor: 'hsl(var(--primary) / 0.3)',
        background: 'hsl(var(--primary) / 0.05)'
      }}
    >
      <div className="flex items-center gap-2.5 min-w-0 flex-1">
        <svg
          aria-hidden="true"
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="shrink-0 text-primary"
        >
          <path d="M12 3v3" />
          <path d="M12 18v3" />
          <path d="M3 12h3" />
          <path d="M18 12h3" />
          <circle cx="12" cy="12" r="4" />
        </svg>
        <div className="text-[13px] leading-snug text-[var(--sig-body)]">
          <strong className="text-[var(--sig-ink)]">New topic?</strong> Signals
          now remembers only the last {state.keep} questions of this chat. A new
          chat keeps it focused.
        </div>
      </div>
      <div className="flex gap-2">
        <Button variant="outline" size="sm" onClick={() => setVisible(false)}>
          Keep going
        </Button>
        <Button
          size="sm"
          onClick={() => {
            setVisible(false);
            startNewChat();
          }}
        >
          Start new chat
        </Button>
      </div>
    </div>
  );
}
